import { useEffect, useRef, useState, type RefObject } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/useAuth';
import type { BusinessSummary } from '../../contexts/AuthTypes';

interface BusinessMenuProps {
    open: boolean;
    onClose: () => void;
    /**
     * The boundary the outside-click check treats as "inside" — must wrap both the trigger (the
     * topbar's business chip) and this menu, exactly like NotificationsPopover's and UserMenu's
     * boundaryRef wrap their own triggers. Without this, a click on the chip bubbles a `mousedown`
     * this component sees as "outside" (its own DOM node doesn't contain the chip) and closes on
     * it — then the chip's own click handler re-opens it a beat later, so the chip could never
     * close an open menu. See NotificationsPopover's identical prop for the full story.
     */
    boundaryRef: RefObject<HTMLElement | null>;
    /**
     * Where the panel sits, and how wide it is. Defaulted to the desktop case — hanging under the
     * chip's wrapper and centred on it — because that is the only trigger on `md:` and up. The
     * mobile entry point (the More sheet) has no chip to hang off, so RedesignLayout passes its
     * own fixed placement instead. Position and width live together here so the two callers can't
     * half-override each other: Tailwind resolves competing width utilities by stylesheet order,
     * not by the order they appear in a className string.
     */
    positionClassName?: string;
}

/**
 * The prototype's "YOUR BUSINESSES" menu, opened from the topbar's business chip.
 *
 * The prototype's per-business subline is "Trade · Bulimba QLD" — a business type and a suburb.
 * **Neither field exists on `BusinessSummary`**, and neither is fetched anywhere else, so there is
 * nothing to go looking for: `sublineFor` below builds that line out of what we actually carry.
 *
 * Unmounted entirely while closed (`open` false renders null), same as NotificationsPopover and
 * UserMenu and for the same reason: there is no transition to preserve, so nothing needs `inert` —
 * an unmounted menu is already out of the tab order and the accessibility tree.
 */
export default function BusinessMenu({ open, onClose, boundaryRef, positionClassName = 'absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[320px]' }: BusinessMenuProps) {
    const menuRef = useRef<HTMLDivElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    const { businesses, activeBusiness, switchBusiness } = useAuth();
    // The id currently being switched to, or null. Doubles as the in-flight flag: switchBusiness
    // hits the server, and a second click while the first is still running would race it (two
    // active-business writes, and whichever response lands last wins), so every row is disabled
    // for the duration rather than only the one that was clicked.
    const [switchingId, setSwitchingId] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;

        // Same focus contract as NotificationsPopover/UserMenu/MoreSheet: capture whatever had
        // focus (the chip, in practice) before moving focus into the menu, and hand it back on
        // close.
        previouslyFocused.current = document.activeElement as HTMLElement | null;
        menuRef.current?.focus();

        return () => {
            previouslyFocused.current?.focus();
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }

        function handleClickOutside(e: MouseEvent) {
            // Checked against boundaryRef (chip + menu), not menuRef (menu only) — see the prop's
            // doc comment for why that distinction is the whole fix.
            if (boundaryRef.current && !boundaryRef.current.contains(e.target as Node)) {
                onClose();
            }
        }

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose, boundaryRef]);

    if (!open) return null;

    const handleSelect = async (business: BusinessSummary) => {
        // The active business just closes the menu: re-switching to what is already active would
        // be a pointless server round-trip, and its row already reads as "current" via the tick.
        if (business.id === activeBusiness?.id) {
            onClose();
            return;
        }
        if (switchingId !== null) return;

        setSwitchingId(business.id);
        try {
            await switchBusiness(business.id);
            onClose();
        } catch {
            // Deliberately stays open on failure — closing here would look exactly like a
            // successful switch, and the chip would still be showing the old business with no
            // explanation. Same treatment as DevelopmentApplicationDetail's toggleSave.
            toast.error('Could not switch business. Please try again.');
        } finally {
            setSwitchingId(null);
        }
    };

    const handleCreate = () => {
        navigate('/add-business');
        onClose();
    };

    return (
        <div
            ref={menuRef}
            role="menu"
            aria-label="Your businesses"
            tabIndex={-1}
            data-testid="business-menu"
            className={`${positionClassName} bg-surface border border-border rounded-[14px] shadow-[0_16px_40px_rgba(28,33,28,.25)] p-2 z-70`}
        >
            <div className="px-3 pt-2 pb-1.5 text-[11.5px] font-bold tracking-[0.06em] text-ink-faint">
                YOUR BUSINESSES
            </div>

            {businesses.map((business) => {
                const isActive = business.id === activeBusiness?.id;
                const subline = sublineFor(business);

                return (
                    <button
                        key={business.id}
                        type="button"
                        role="menuitem"
                        disabled={switchingId !== null}
                        onClick={() => void handleSelect(business)}
                        className="flex items-center gap-[11px] w-full text-left px-3 py-2.5 rounded-[10px] disabled:opacity-60"
                    >
                        <span aria-hidden="true" className="size-[30px] rounded-lg bg-sidebar text-white grid place-items-center text-xs font-bold flex-none">
                            {business.name?.[0]?.toUpperCase() ?? '?'}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block text-[13.5px] font-bold text-ink truncate">{business.name}</span>
                            {/* No subline element at all when there is nothing to put in it — an
                                empty div or a placeholder dash would just be a ragged blank line. */}
                            {subline && <span className="block text-xs text-ink-muted truncate">{subline}</span>}
                        </span>
                        {isActive && (
                            <span className="text-sm font-bold text-leaf flex-none">
                                <span aria-hidden="true">✓</span>
                                {/* A sighted user reads the tick; without this a screen-reader user
                                    would have no way to tell which business is the active one. */}
                                <span className="sr-only">Current business</span>
                            </span>
                        )}
                    </button>
                );
            })}

            <button
                type="button"
                role="menuitem"
                onClick={handleCreate}
                className="flex items-center gap-[11px] w-full text-left px-3 py-[11px] rounded-[10px] border-t border-border-inner mt-1.5"
            >
                <span aria-hidden="true" className="size-[30px] rounded-lg border-[1.5px] border-dashed border-border-strong text-ink-faint grid place-items-center text-[15px] flex-none">
                    +
                </span>
                <span className="text-[13.5px] font-bold text-leaf">Create new business</span>
            </button>
        </div>
    );
}

/**
 * The subline under a business's name.
 *
 * The prototype shows "Trade · Bulimba QLD". `BusinessSummary` carries no business type and no
 * suburb — those fields do not exist on this type, on the endpoint behind it, or anywhere else in
 * the client — so this is built from the fields we do have: `planName` and `state` joined with
 * " · ", falling back to `subscriptionStatus` when neither is set. Returns null (no subline row at
 * all) when a business has none of the three, rather than rendering an empty line or a dash.
 */
function sublineFor(business: BusinessSummary): string | null {
    const parts = [business.planName, business.state]
        .map((part) => part?.trim())
        .filter((part): part is string => !!part);

    if (parts.length > 0) return parts.join(' · ');

    return business.subscriptionStatus?.trim() || null;
}
